import type {
  AgreementSignatureDto,
  ChangePasswordInput,
  ForgotPasswordInput,
  LoginRequest,
  RegisterInput,
  ResetPasswordInput,
  SessionUserDto,
  SignAgreementInput,
  UpdateProfileInput,
} from '@shared/contracts/auth';
import type { Page } from '@shared/contracts/common';
import type {
  CreateRoomInput,
  RoomDto,
  RoomUtilizationReportDto,
  UpdateRoomInput,
} from '@shared/contracts/room';
import type {
  AdminFaqDto,
  AdminInstructorReviewDto,
  AdminTestimonialDto,
  CreateFaqInput,
  CreateInstructorReviewInput,
  CreateTestimonialInput,
  SubmitTestimonialInput,
  UpdateFaqInput,
  UpdateTestimonialInput,
} from '@shared/contracts/content';
import type {
  AdminMemberDto,
  ListMembersQuery,
  MemberPasswordResetDto,
  AccountDeletionResultDto,
  SetPermissionsInput,
  UpdateMemberInput,
} from '@shared/contracts/member';
import type {
  StudioProfileDto,
  UpdateStudioProfileInput,
} from '@shared/contracts/studio';
import type {
  GalleryImageDto,
  MediaAssetDto,
  SetGalleryInput,
  UpdateMediaInput,
} from '@shared/contracts/media';
import type {
  FaqDto,
  PublicProgramCategoryDto,
  PublicProgramDetailDto,
  TestimonialDto,
} from '@shared/contracts/program';
import type {
  AddPackagesInput,
  ApplyJointMakeupInput,
  ApplyMakeupInput,
  CreateEnrollmentInput,
  CreatedEnrollmentDto,
  DeleteEnrollmentInput,
  EnrollmentDto,
  ListEnrollmentsQuery,
  ProgramCategoryDto,
  RecordAttendanceInput,
  ScheduleLessonInput,
  UpdateEnrollmentInput,
} from '@shared/contracts/enrollment';
import type {
  AdminProgramCatalogDto,
  AdminProgramDto,
  CreatePackageInput,
  CreateProgramCategoryInput,
  CreateProgramInput,
  PackageDto,
  ProgramCategoryOptionDto,
  UpdatePackageInput,
  UpdateProgramInput,
} from '@shared/contracts/programCatalog';
import type {
  MemberPackageDto,
  MyLessonsQuery,
  ScheduleLessonDto,
  ScheduleQuery,
} from '@shared/contracts/schedule';
import type {
  AssignableInstructorAccountDto,
  CreateInstructorInput,
  InstructorAccountDto,
  PreviousAccountOutcome,
  UpdateInstructorInput,
  UpdateOwnInstructorInput,
  WeeklyShiftsInput,
} from '@shared/contracts/scheduling';
import type { StudioStatsDto } from '@shared/contracts/report';
import type { CancelLessonInput, UpdateLessonInput } from '@shared/contracts/training';
import { api, newIdempotencyKey, qs } from '../lib/apiClient';

export type { StudioStatsDto };

export interface InstructorDto {
  id: string;
  name: string;
  title: string;
  bio: string;
  specialities: string[];
  avatarUrl: string | null;
  color: string;
  rating: number;
  experienceYears: number;
  certifications: string[];
  /** Tanıtım sitesindeki kısa kişisel cümle. */
  note: string | null;
  /** Verdiği eğitimler ("uzmanlık") — sitedeki "odak" listesi buradan türetilir. */
  programs: Array<{ id: string; slug: string; name: string }>;
  isActive: boolean;
  reviews: Array<{ id: string; authorName: string; rating: number; comment: string; date: string }>;
  shifts: Array<{
    id: string;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    slotDurationMinutes: number;
    breakDurationMinutes: number;
    isActive: boolean;
  }>;
}

/** Yönetim listesi satırı — profil + bağlı hesap (herkese açık uçta yok). */
export interface AdminInstructorDto extends InstructorDto {
  account: InstructorAccountDto;
}

/** Profil başka hesaba taşındı; eski hesaba ne olduğu da dönüyor. */
export interface AssignInstructorAccountResultDto {
  instructor: AdminInstructorDto;
  previousAccount: PreviousAccountOutcome;
}

export const endpoints = {
  // Kimlik
  login: (body: LoginRequest) => api.post<SessionUserDto>('/auth/login', body),
  register: (body: RegisterInput) => api.post<SessionUserDto>('/auth/register', body),
  logout: () => api.post<{ loggedOut: true }>('/auth/logout'),
  me: () => api.get<SessionUserDto>('/auth/me'),
  updateProfile: (body: UpdateProfileInput) => api.patch<SessionUserDto>('/auth/me/profile', body),
  changePassword: (body: ChangePasswordInput) =>
    api.post<SessionUserDto>('/auth/me/password', body),
  forgotPassword: (body: ForgotPasswordInput) =>
    api.post<{ requested: true }>('/auth/forgot-password', body),
  resetPassword: (body: ResetPasswordInput) =>
    api.post<{ reset: true }>('/auth/reset-password', body),
  /* Üyelik sözleşmesi imzası — güncel oturumu döndürür (kapı kalkar). */
  signAgreement: (body: SignAgreementInput) =>
    api.post<SessionUserDto>('/auth/me/agreement', body),
  myAgreement: () => api.get<AgreementSignatureDto | null>('/auth/me/agreement'),

  // Katalog
  instructors: () => api.get<InstructorDto[]>('/instructors'),

  // Yönetim
  createInstructor: (body: CreateInstructorInput) =>
    api.post<InstructorDto>('/instructors', body),
  adminInstructors: () => api.get<AdminInstructorDto[]>('/admin/instructors'),
  assignableInstructorAccounts: () =>
    api.get<AssignableInstructorAccountDto[]>('/admin/instructors/assignable-accounts'),
  assignInstructorAccount: (id: string, userId: string) =>
    api.post<AssignInstructorAccountResultDto>(`/admin/instructors/${id}/account`, { userId }),
  /* Sunucu ucu (`PATCH /instructors/:id`) baştan beri vardı ama istemcide
     karşılığı yoktu — yani eğitmen bilgisi oluşturulduktan sonra panelden
     hiç düzenlenemiyordu. Fotoğraf seçimi bunu gerektirdi. */
  updateInstructor: (id: string, body: UpdateInstructorInput) =>
    api.patch<InstructorDto>(`/instructors/${id}`, body),
  /* Haftalık çalışma saatleri — tüm hafta tek istekte (Eğitmen Kadrosu). */
  updateInstructorShifts: (id: string, body: WeeklyShiftsInput) =>
    api.put<{ saved: true }>(`/instructors/${id}/shifts`, body),

  /* Eğitmenin KENDİ profili (Profilim). Kimlik oturumdan çözülür. */
  myInstructorProfile: () => api.get<InstructorDto>('/instructors/me'),
  updateMyInstructorProfile: (body: UpdateOwnInstructorInput) =>
    api.patch<InstructorDto>('/instructors/me', body),
  updateMyShifts: (body: WeeklyShiftsInput) => api.put<InstructorDto>('/instructors/me/shifts', body),
  uploadMyAvatar: (file: File) =>
    api.post<InstructorDto>('/instructors/me/avatar', undefined, {
      rawBody: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'X-Filename': encodeURIComponent(file.name),
      },
    }),
  /* Program kayıtları — yeni omurga.
     Yazma uçlarının tamamı idempotency anahtarı taşır: bir kayıt 10 ders +
     20 katılımcı satırı üretiyor ve çift tıklama bunu ikiye katlardı. */
  programCatalog: () => api.get<ProgramCategoryDto[]>('/enrollments/catalog'),
  /* Kayıt düzenleme ve silme — ortak takvim (yönetici + tüm eğitmenler). */
  updateEnrollment: (id: string, body: UpdateEnrollmentInput) =>
    api.patch<EnrollmentDto>(`/enrollments/${id}`, body),
  deleteEnrollment: (id: string, body: DeleteEnrollmentInput) =>
    api.post<{ deleted: true }>(`/enrollments/${id}/delete`, body),
  updateLesson: (id: string, body: UpdateLessonInput) =>
    api.patch<{ updated: true }>(`/enrollments/lessons/${id}`, body),
  cancelLesson: (id: string, body: CancelLessonInput) =>
    api.post<{ cancelled: true }>(`/enrollments/lessons/${id}/cancel`, body),
  /* Ders Programı — tek takvim. Personel süzgeçli; üye yalnızca kendini görür. */
  schedule: (query: ScheduleQuery) =>
    api.get<ScheduleLessonDto[]>(
      `/schedule${qs({
        from: query.from,
        to: query.to,
        trainerId: query.trainerId,
        roomId: query.roomId,
        programId: query.programId,
        studentId: query.studentId,
        includeCancelled: query.includeCancelled ? 'true' : undefined,
      })}`,
    ),
  myLessons: (query: MyLessonsQuery) =>
    api.get<ScheduleLessonDto[]>(`/me/lessons${qs({ from: query.from, to: query.to })}`),
  myPackages: () => api.get<MemberPackageDto[]>('/me/packages'),
  /* Üyenin kendi mazeret bildirimi — dersten 24 saat öncesine kadar. */
  requestMakeup: (lessonId: string) =>
    api.post<{ shiftedLessons: number; makeupLessonId: string | null }>(
      `/me/lessons/${lessonId}/makeup`,
      {},
      { idempotencyKey: newIdempotencyKey() },
    ),
  /* Yönetici önizlemesi ("üye olarak görüntüle") — üyenin gördüğü kapsamla. */
  memberLessons: (memberId: string, query: MyLessonsQuery) =>
    api.get<ScheduleLessonDto[]>(
      `/admin/members/${memberId}/lessons${qs({ from: query.from, to: query.to })}`,
    ),
  memberPackages: (memberId: string) =>
    api.get<MemberPackageDto[]>(`/admin/members/${memberId}/packages`),

  /* Eğitimler & Paketler — yönetici kataloğu (fiyatlı, pasifler dahil). */
  adminPrograms: () => api.get<AdminProgramCatalogDto>('/admin/programs'),
  createProgramCategory: (body: CreateProgramCategoryInput) =>
    api.post<ProgramCategoryOptionDto>('/admin/program-categories', body),
  createProgram: (body: CreateProgramInput) => api.post<AdminProgramDto>('/admin/programs', body),
  updateProgram: (id: string, body: UpdateProgramInput) =>
    api.patch<AdminProgramDto>(`/admin/programs/${id}`, body),
  createPackage: (programId: string, body: CreatePackageInput) =>
    api.post<PackageDto>(`/admin/programs/${programId}/packages`, body),
  updatePackage: (id: string, body: UpdatePackageInput) =>
    api.patch<PackageDto>(`/admin/packages/${id}`, body),

  /* Tanıtım sitesi — kimlik gerektirmez.
     `programCatalog` ile aynı veriyi döndürmüyor: o uç `requireAuth` arkasında
     ve eğitmenin kayıt formunu besliyor (operasyonel alanlar), buradakiler
     ziyaretçiye gösterilecek tanıtım alanlarını taşıyor. */
  publicPrograms: () => api.get<PublicProgramCategoryDto[]>('/programs'),
  publicProgram: (slug: string) => api.get<PublicProgramDetailDto>(`/programs/${slug}`),
  faqs: () => api.get<FaqDto[]>('/faqs'),
  testimonials: () => api.get<TestimonialDto[]>('/testimonials'),
  /* Üyenin siteden yorum göndermesi — taslak doğar, yönetici onaylar. */
  submitTestimonial: (body: SubmitTestimonialInput) =>
    api.post<AdminTestimonialDto>('/testimonials', body),
  studio: () => api.get<StudioProfileDto>('/studio'),
  updateStudio: (body: UpdateStudioProfileInput) => api.put<StudioProfileDto>('/studio', body),
  /* Salonlar. Kayıt formu ve yönetim aynı ucu kullanıyor: form yalnızca
     aktifleri görür, yönetici `includeInactive` ile pasifleri de ister. */
  rooms: (includeInactive = false) =>
    api.get<RoomDto[]>(`/rooms${qs({ includeInactive: includeInactive ? true : undefined })}`),
  createRoom: (body: CreateRoomInput) => api.post<RoomDto>('/rooms', body),
  updateRoom: (id: string, body: UpdateRoomInput) => api.patch<RoomDto>(`/rooms/${id}`, body),
  deleteRoom: (id: string) => api.delete<{ deleted: true }>(`/rooms/${id}`),
  roomUtilization: (from: string, to: string) =>
    api.get<RoomUtilizationReportDto>(`/rooms/utilization${qs({ from, to })}`),

  adminTestimonials: () => api.get<AdminTestimonialDto[]>('/admin/testimonials'),
  createTestimonial: (body: CreateTestimonialInput) =>
    api.post<AdminTestimonialDto>('/admin/testimonials', body),
  updateTestimonial: (id: string, body: UpdateTestimonialInput) =>
    api.patch<AdminTestimonialDto>(`/admin/testimonials/${id}`, body),
  deleteTestimonial: (id: string) => api.delete<{ deleted: true }>(`/admin/testimonials/${id}`),

  /* Üye yönetimi. Sayfalama cursor tabanlı: bir yıl kullanan stüdyoda üye
     listesi yüzlerce satıra çıkar ve offset derin sayfalarda kayıt atlar. */
  adminMembers: (query: Partial<ListMembersQuery>) =>
    api.get<Page<AdminMemberDto>>(
      `/admin/members${qs({
        cursor: query.cursor,
        limit: query.limit,
        search: query.search,
        permission: query.permission,
        includeInactive: query.includeInactive,
      })}`,
    ),
  updateMember: (id: string, body: UpdateMemberInput) =>
    api.patch<AdminMemberDto>(`/admin/members/${id}`, body),
  resetMemberPassword: (id: string) =>
    api.post<MemberPasswordResetDto>(`/admin/members/${id}/password-reset`, {}),

  /* Yetkiler — tam durum (üç alan birden) gönderilir, fark değil. */
  setMemberPermissions: (id: string, body: SetPermissionsInput) =>
    api.patch<AdminMemberDto>(`/admin/members/${id}/permissions`, body),
  deleteMember: (id: string) => api.delete<AccountDeletionResultDto>(`/admin/members/${id}`),

  adminInstructorReviews: () =>
    api.get<AdminInstructorReviewDto[]>('/admin/instructor-reviews'),
  createInstructorReview: (body: CreateInstructorReviewInput) =>
    api.post<AdminInstructorReviewDto>('/admin/instructor-reviews', body),
  deleteInstructorReview: (id: string) =>
    api.delete<{ deleted: true }>(`/admin/instructor-reviews/${id}`),

  adminFaqs: () => api.get<AdminFaqDto[]>('/admin/faqs'),
  createFaq: (body: CreateFaqInput) => api.post<AdminFaqDto>('/admin/faqs', body),
  updateFaq: (id: string, body: UpdateFaqInput) => api.patch<AdminFaqDto>(`/admin/faqs/${id}`, body),
  deleteFaq: (id: string) => api.delete<{ deleted: true }>(`/admin/faqs/${id}`),

  /* Medya kutuphanesi.

     Yukleme govdesi JSON DEGIL, dosyanin ham baytlaridir; ad ve alt metni bu
     yuzden basliklarda gider. HTTP basliklari latin-1 oldugu icin Turkce ad
     `encodeURIComponent` ile kodlanmak ZORUNDA — kodlanmamis bir "s" basligi
     bozar ve istek daha sunucuya varmadan duser. */
  media: (inGallery?: boolean) =>
    api.get<MediaAssetDto[]>(`/media${qs({ inGallery: inGallery === true ? true : undefined })}`),
  uploadMedia: (file: File, alt?: string) =>
    api.post<MediaAssetDto>('/media', undefined, {
      rawBody: file,
      headers: {
        'Content-Type': file.type || 'application/octet-stream',
        'X-Filename': encodeURIComponent(file.name),
        ...(alt ? { 'X-Alt-Text': encodeURIComponent(alt) } : {}),
      },
    }),
  updateMedia: (id: string, body: UpdateMediaInput) =>
    api.patch<MediaAssetDto>(`/media/${id}`, body),
  deleteMedia: (id: string) => api.delete<{ deleted: true }>(`/media/${id}`),
  setGallery: (mediaIds: string[]) =>
    api.put<GalleryImageDto[]>('/media/gallery', { mediaIds } satisfies SetGalleryInput),
  gallery: () => api.get<GalleryImageDto[]>('/gallery'),
  students: () => api.get<Array<{ id: string; name: string }>>('/students'),
  enrollments: (query: ListEnrollmentsQuery = {}) =>
    api.get<EnrollmentDto[]>(
      `/enrollments${qs({
        trainerId: query.trainerId,
        programId: query.programId,
        studentId: query.studentId,
        status: query.status,
      })}`,
    ),
  enrollment: (id: string) => api.get<EnrollmentDto>(`/enrollments/${id}`),
  createEnrollment: (body: CreateEnrollmentInput) =>
    api.post<CreatedEnrollmentDto>('/enrollments', body, { idempotencyKey: newIdempotencyKey() }),
  /* Grupta katılım, diğerlerinde paket yenileme. */
  addPackages: (enrollmentId: string, body: AddPackagesInput) =>
    api.post<CreatedEnrollmentDto>(`/enrollments/${enrollmentId}/packages`, body, {
      idempotencyKey: newIdempotencyKey(),
    }),
  /* Esnek plan: tek ders planlama. */
  scheduleLesson: (enrollmentId: string, body: ScheduleLessonInput) =>
    api.post<EnrollmentDto>(`/enrollments/${enrollmentId}/lessons`, body, {
      idempotencyKey: newIdempotencyKey(),
    }),
  applyMakeup: (enrollmentId: string, body: ApplyMakeupInput) =>
    api.post<{ shiftedLessons: number }>(`/enrollments/${enrollmentId}/makeup`, body, {
      idempotencyKey: newIdempotencyKey(),
    }),
  applyJointMakeup: (enrollmentId: string, body: ApplyJointMakeupInput) =>
    api.post<{ shiftedLessons: number }>(`/enrollments/${enrollmentId}/makeup-all`, body, {
      idempotencyKey: newIdempotencyKey(),
    }),
  recordAttendance: (enrollmentId: string, lessonId: string, body: RecordAttendanceInput) =>
    api.patch<unknown>(`/enrollments/${enrollmentId}/lessons/${lessonId}/attendance`, body),

  // Raporlar
  stats: () => api.get<StudioStatsDto>('/reports/stats'),
  attendanceReportUrl: '/api/v1/reports/attendance.csv',
} as const;
